// Lightweight formatters for PDF cell contents. The portal's richer
// dateTimeUtils helpers are React-aware (they read AuthContext); the PDF
// renderer runs outside the React tree, so we pass timezone/currency in
// through props. Keep these dependency-free.

import dateTimeUtils from '../dateTimeUtils';
import { cnicDigits, formatCnic } from '../cnic';

export function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return dateTimeUtils.formatDateForDisplay(iso);
  } catch {
    return '—';
  }
}

/** The date written out, as a letter or receipt carries it — "1 November 2026". */
export function fmtLongDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return dateTimeUtils.formatDateForDisplay(iso, undefined, 'd MMMM yyyy');
  } catch {
    return '—';
  }
}

export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return dateTimeUtils.formatDateTimeForDisplay(iso);
  } catch {
    return '—';
  }
}

export function fmtMoney(value: number | null | undefined, currency = 'PKR'): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  try {
    // Whole amounts print without decimals; paisa print as two ("1,250.50", never "1,250.5").
    const whole = Number.isInteger(Math.round(Number(value) * 100) / 100);
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  } catch {
    return `${currency} ${Number(value).toLocaleString()}`;
  }
}

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function underHundred(n: number): string {
  return n < 20 ? ONES[n] : [TENS[Math.floor(n / 10)], ONES[n % 10]].filter(Boolean).join('-');
}

function underThousand(n: number): string {
  const h = Math.floor(n / 100);
  return [h ? `${ONES[h]} Hundred` : '', underHundred(n % 100)].filter(Boolean).join(' ');
}

/** 2,045,075 → "Twenty Lakh Forty-Five Thousand Seventy-Five" (South Asian grouping). */
function wholeInWords(n: number): string {
  if (n === 0) return 'Zero';
  const parts: string[] = [];
  const crore = Math.floor(n / 1e7);
  if (crore) parts.push(`${wholeInWords(crore)} Crore`);
  const lakh = Math.floor((n % 1e7) / 1e5);
  if (lakh) parts.push(`${underHundred(lakh)} Lakh`);
  const thousand = Math.floor((n % 1e5) / 1e3);
  if (thousand) parts.push(`${underHundred(thousand)} Thousand`);
  const rest = n % 1e3;
  if (rest) parts.push(underThousand(rest));
  return parts.join(' ');
}

const CURRENCY_WORDS: Record<string, [string, string]> = { PKR: ['Rupees', 'Paisa'], INR: ['Rupees', 'Paise'] };

/** The amount as written on a receipt: "Rupees Twenty Thousand Four Hundred Seventy-Five Only". */
export function fmtAmountInWords(value: number | null | undefined, currency = 'PKR'): string {
  if (value == null || Number.isNaN(Number(value))) return '';
  const minor = Math.round(Math.abs(Number(value)) * 100);
  const [major, sub] = CURRENCY_WORDS[currency] ?? [currency, 'cents'];
  const whole = Math.floor(minor / 100);
  const cents = minor % 100;
  return `${major} ${wholeInWords(whole)}${cents ? ` and ${underHundred(cents)} ${sub}` : ''} Only`;
}

export function fmtNumber(value: number | null | undefined, digits = 0): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Marks as entered — whole or half marks show as given: 17, 17.5, 8.25. */
export function fmtMarks(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function fmtPercent(value: number | null | undefined, digits = 1): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(digits)}%`;
}

/** NADRA layout for a 13-digit CNIC / B-Form ("35202-1234567-1"). Values
 *  that are not 13 digits are returned as entered rather than guessed at. */
export function fmtCnic(value?: string | null): string {
  if (!value) return '—';
  return cnicDigits(value).length === 13 ? formatCnic(value) : value;
}

/** Schools often name classes by number alone ("9"). On paper "Applying
 *  for 9" reads wrong, so bare numbers get a "Class" prefix; names that
 *  already say what they are ("Grade 7", "Nursery") pass through. */
export function fmtClassName(name?: string | null): string {
  if (!name) return '';
  return /^\d+$/.test(name.trim()) ? `Class ${name.trim()}` : name;
}

/** Title-case a snake-cased or kebab-cased string ("under_review" → "Under review"). */
export function humanize(input?: string | null): string {
  if (!input) return '';
  const spaced = input.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
